import { useEffect, useState } from 'react';
import { MetaRow } from '@/ui';
import { StudioButton as Button } from './styles';
import type { StudioResource, Viewpoint } from './contracts';
import { studio } from './service';
import { useStudioTask } from './useStudioTask';
import { Field, Note, Panel, Row } from './styles';
export function PerspectiveEditor({
  userId,
  onChoose,
  initialSlug,
}: {
  userId: string;
  onChoose: (r: StudioResource) => void;
  initialSlug?: string;
}) {
  const [items, setItems] = useState<StudioResource[]>([]);
  const [next, setNext] = useState<string>();
  const [resource, setResource] = useState<StudioResource>();
  const [title, setTitle] = useState('');
  const [common, setCommon] = useState('');
  const [views, setViews] = useState<Viewpoint[]>([]);
  const [handle, setHandle] = useState('');
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [consent, setConsent] = useState(false);
  const [changed, setChanged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const task = useStudioTask();
  const { setError } = task;
  useEffect(() => {
    let active = true;
    studio.list('perspective-splits').then(
      (r) => {
        if (active) {
          setItems(r.items);
          setNext(r.next);
        }
      },
      () => {
        if (active) setError('Could not load perspectives. Close and reopen to retry.');
      },
    );
    if (initialSlug)
      studio.get('perspective-splits', initialSlug).then(
        (r) => {
          if (!active) return;
          setResource(r);
          setTitle(r.title);
          setCommon(r.data.commonGround ?? '');
          setViews(r.data.viewpoints?.filter((v) => v.authorship === 'author') ?? []);
        },
        () => {
          if (active)
            setError('Could not open this perspective. Check your invitation or refresh.');
        },
      );
    return () => {
      active = false;
    };
  }, [setError, initialSlug]);
  function select(r?: StudioResource) {
    setResource(r);
    setTitle(r?.title ?? '');
    setCommon(r?.data.commonGround ?? '');
    setViews(r?.data.viewpoints?.filter((v) => v.authorship === 'author') ?? []);
    setChanged(false);
    setConsent(false);
    setLabel('');
    setText('');
    setDeleting(false);
  }
  function accept(r: StudioResource) {
    setItems([...items.filter((v) => v.slug !== r.slug), r]);
    select(r);
  }
  const owner = !resource || resource.role === 'owner';
  const mine = resource?.data.contributors?.find((c) => c.person.id === userId);
  const editable = !resource || resource.data.state === 'draft';
  const act = (action: string, values: Record<string, unknown> = {}) =>
    resource &&
    void task.run(() => studio.action('perspective-splits', resource, action, values), accept);
  return (
    <fieldset
      disabled={task.busy}
      style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'grid', gap: 16 }}
    >
      <Note>
        Write labelled viewpoints yourself or invite people to contribute with their own attribution
        and consent.
      </Note>
      <Row>
        {items.map((r) => (
          <Button
            key={r.slug}
            onClick={() => void task.run(() => studio.get('perspective-splits', r.slug), select)}
          >
            {r.title}
          </Button>
        ))}
        {next && (
          <Button
            onClick={() =>
              void task.run(
                () => studio.list('perspective-splits', next),
                (r) => {
                  setItems([...items, ...r.items]);
                  setNext(r.next);
                },
              )
            }
          >
            Load more
          </Button>
        )}
        <Button onClick={() => select()}>New perspective split</Button>
      </Row>
      {owner && editable && (
        <>
          <Field>
            Working title
            <input
              maxLength={120}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setChanged(true);
              }}
            />
          </Field>
          {views.map((v, i) => (
            <Panel key={v.id}>
              <Field>
                Viewpoint {i + 1} label
                <input
                  maxLength={100}
                  value={v.label ?? ''}
                  onChange={(e) => {
                    setViews(views.map((x, n) => (n === i ? { ...x, label: e.target.value } : x)));
                    setChanged(true);
                  }}
                />
              </Field>
              <Field>
                Viewpoint {i + 1} text
                <textarea
                  maxLength={2000}
                  value={v.text ?? ''}
                  onChange={(e) => {
                    setViews(views.map((x, n) => (n === i ? { ...x, text: e.target.value } : x)));
                    setChanged(true);
                  }}
                />
              </Field>
              <Button
                variant="ghost"
                onClick={() => {
                  setViews(views.filter((_, n) => n !== i));
                  setChanged(true);
                }}
              >
                Remove viewpoint {i + 1}
              </Button>
            </Panel>
          ))}
          <Button
            disabled={views.length >= 8}
            onClick={() => {
              setViews([...views, { id: crypto.randomUUID(), label: '', text: '' }]);
              setChanged(true);
            }}
          >
            Add author-written viewpoint
          </Button>
          <Field>
            Common ground (optional)
            <textarea
              maxLength={1000}
              value={common}
              onChange={(e) => {
                setCommon(e.target.value);
                setChanged(true);
              }}
            />
          </Field>
          <Button
            disabled={
              task.busy || !title.trim() || views.some((v) => !v.label?.trim() || !v.text?.trim())
            }
            onClick={() =>
              void task.run(
                () =>
                  studio.save(
                    'perspective-splits',
                    title,
                    {
                      viewpoints: views.map((v) => ({ id: v.id, label: v.label, text: v.text })),
                      commonGround: common,
                    },
                    resource,
                  ),
                accept,
              )
            }
          >
            Save perspectives
          </Button>
        </>
      )}
      {resource?.data.viewpoints
        ?.filter((v) => v.authorship !== 'author' || !owner || !editable)
        .map((v) => (
          <Panel key={v.id}>
            <strong>{v.unavailable ? 'Perspective unavailable' : v.label}</strong>
            {!v.unavailable && (
              <>
                <Note>
                  {v.authorship === 'author' ? 'Written' : 'Contributed'} by @
                  {v.author?.handle?.replace(/^@/, '')}
                </Note>
                <p>{v.text}</p>
              </>
            )}
          </Panel>
        ))}
      {resource?.data.contributors?.map((p) => (
        <Row key={p.person.id}>
          <Note>
            <MetaRow>
              {`@${p.person.handle}`}
              {p.state}
            </MetaRow>
          </Note>
          {owner && editable && ['pending', 'accepted'].includes(p.state) && (
            <Button
              disabled={task.busy || changed}
              onClick={() => act('remove_contributor', { handle: p.person.handle })}
            >
              Remove invitation
            </Button>
          )}
        </Row>
      ))}
      {owner && resource && editable && (
        <Row>
          <Field>
            Invite contributor
            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="Handle"
            />
          </Field>
          <Button
            disabled={task.busy || changed || !handle.trim()}
            onClick={() => act('invite', { handle })}
          >
            Invite
          </Button>
        </Row>
      )}
      {!owner && mine?.state === 'pending' && (
        <Row>
          <Button disabled={task.busy} onClick={() => act('respond', { state: 'accepted' })}>
            Accept invitation
          </Button>
          <Button disabled={task.busy} onClick={() => act('respond', { state: 'declined' })}>
            Decline
          </Button>
        </Row>
      )}
      {!owner && mine?.state === 'accepted' && (
        <>
          {editable && (
            <>
              <Field>
                Your label
                <input
                  maxLength={100}
                  value={label}
                  onChange={(e) => {
                    setLabel(e.target.value);
                    setConsent(false);
                  }}
                />
              </Field>
              <Field>
                Your perspective
                <textarea
                  maxLength={2000}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    setConsent(false);
                  }}
                />
              </Field>
              <label>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />{' '}
                I consent to publishing this exact text with my attribution.
              </label>
              <Button
                disabled={task.busy || !consent || !label.trim() || !text.trim()}
                onClick={() => act('submit_viewpoint', { label, text, consentToPublish: true })}
              >
                Submit viewpoint
              </Button>
            </>
          )}
          <Button disabled={task.busy} onClick={() => act('respond', { state: 'withdrawn' })}>
            Withdraw contribution and consent
          </Button>
        </>
      )}
      {task.error && <p role="alert">{task.error}</p>}
      {owner && resource && editable && (
        <>
          <Button variant="ghost" disabled={task.busy} onClick={() => setDeleting(true)}>
            Delete private perspectives
          </Button>
          {deleting && (
            <Row>
              <Note>Delete this private draft and its invitations?</Note>
              <Button
                variant="danger"
                disabled={task.busy}
                onClick={() =>
                  void task.run(
                    () => studio.remove('perspective-splits', resource),
                    () => {
                      setItems(items.filter((r) => r.slug !== resource.slug));
                      select();
                    },
                  )
                }
              >
                Confirm delete
              </Button>
              <Button onClick={() => setDeleting(false)}>Cancel</Button>
            </Row>
          )}
        </>
      )}
      {owner && resource && (
        <>
          <Note>
            At least two available viewpoints and resolved invitations are required to attach.
          </Note>
          <Row>
            <Button
              disabled={task.busy}
              onClick={() =>
                void task.run(() => studio.get('perspective-splits', resource.slug), select)
              }
            >
              Refresh to review
            </Button>
            <Button
              variant="primary"
              disabled={
                task.busy ||
                changed ||
                !editable ||
                (resource.data.viewpoints?.filter((v) => !v.unavailable).length ?? 0) < 2 ||
                resource.data.contributors?.some((c) => c.state === 'pending')
              }
              onClick={() => onChoose(resource)}
            >
              Attach reviewed perspectives
            </Button>
          </Row>
        </>
      )}
    </fieldset>
  );
}
